import { AC_CARD_STATUSES, type AcCardStatus } from "./accreditation";
import type { RoleCode } from "./auth";

/**
 * Participant accounts (user, 2026-11-03).
 *
 * The moment any accreditation card is created for a person (any category,
 * any status, DRAFT included) that PERSON gets a login: one per person
 * (`people.user_id`), role PARTICIPANT, username = the digits of the card
 * number (`formatCardNumber`, `000111`), a generated temporary password that
 * must be changed on first sign-in. Until it is changed, the temporary
 * password is kept ENCRYPTED (`users.temp_password_enc`) and shown on the
 * record as the person's UNIQUE CODE; changing it clears the column, and the
 * code then reads VERIFIED.
 */

/**
 * PENDING  — a login exists and still has its temporary password (the code).
 * VERIFIED — a login exists and its owner has set their own password.
 * NONE     — the person has no login.
 */
export const UNIQUE_CODE_STATES = ["PENDING", "VERIFIED", "NONE"] as const;
export type UniqueCodeState = (typeof UNIQUE_CODE_STATES)[number];

/**
 * The active roles that may READ the code. ORGANIZATION only for a card in
 * its scope — every record read is `cardInScope` already, so a row an NOC
 * sees is one of its own. Everyone else (OPERATION included) gets the state
 * with `code: null`.
 */
export const UNIQUE_CODE_READ_ROLES = ["SUPER_ADMIN", "ADMIN", "ORGANIZATION"] as const satisfies readonly RoleCode[];
export const canReadUniqueCode = (role: RoleCode | null | undefined): boolean =>
	role != null && (UNIQUE_CODE_READ_ROLES as readonly RoleCode[]).includes(role);

/**
 * `unique_code` on every accreditation list row and record. Always an object;
 * `code` is the temporary password, set only while PENDING and only for a
 * reader in `UNIQUE_CODE_READ_ROLES`. Never in the CSV export or a report.
 */
export interface AccreditationUniqueCode {
	state: UniqueCodeState;
	code: string | null;
}

/**
 * Card statuses that ended: a person whose cards are ALL in one of these has
 * their PARTICIPANT login INACTIVE (the database flips it, trigger on
 * `ac_cards`, sessions revoked); a card back in any other status makes it
 * ACTIVE again. The same pair the records' "live" counts exclude.
 */
export const AC_CARD_ENDED_STATUSES = ["REJECTED", "WITHDRAWN"] as const satisfies readonly AcCardStatus[];
export const PARTICIPANT_LIVE_CARD_STATUSES = AC_CARD_STATUSES.filter(
	(s) => !(AC_CARD_ENDED_STATUSES as readonly AcCardStatus[]).includes(s),
) as readonly AcCardStatus[];

/** All digits: the participant username shape. A STAFF login may never take one (POST /users → 422), so it can never collide with a card number. */
export const isCardNumberUsername = (username: string): boolean => /^\d+$/.test(username.trim());
